/**
 * @fileoverview 应用根组件
 * @description 配置全局路由、主题、国际化和状态管理
 * MVC 架构：App.tsx 作为路由控制器，负责页面级路由分发
 * @module App
 */

import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Route, Switch, useLocation, Redirect } from 'wouter';
import { useEffect, useRef, lazy, Suspense } from 'react';
import { toast } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary';
import DebugConsole from './components/DebugConsole';
import TelegramAutoLogin from './components/TelegramAutoLogin';
import ServerStatusGuard from './components/ServerStatusGuard';
import { ThemeProvider } from './contexts/ThemeContext';
import { useAppStore } from './stores/useAppStore';
import { useTranslation } from 'react-i18next';
import { TonConnectUIProvider } from '@tonconnect/ui-react';
import { TG_MINIAPP_URL } from './lib/tgConfig';
import { TON_MANIFEST_URL } from './lib/chainUrls';

const DEFAULT_TWA_RETURN_URL = TG_MINIAPP_URL as `${string}://${string}`;

// 页面组件（View 层）
// 注意：含 wagmi/viem 依赖的页面必须 lazy 加载，避免 TG WebView 启动时加载 wagmi 导致崩溃
import Home from './pages/Home';
import Markets from './pages/Markets';
const MarketDetail = lazy(() => import('./pages/MarketDetail'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Profile = lazy(() => import('./pages/Profile'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
// Wallet 页面使用懒加载，避免 @tonconnect/ui-react 在所有页面启动时加载（TG WebView 兼容性问题）
const Wallet = lazy(() => import('./pages/Wallet'));
import Leaderboard from './pages/Leaderboard';
const Invite = lazy(() => import('./pages/Invite'));
const About = lazy(() => import('./pages/About'));
import NotFound from './pages/NotFound';
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));

// 后台管理页面（全部 lazy 加载，减小主 bundle 体积）
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminMarkets = lazy(() => import('./pages/admin/AdminMarkets'));
const AdminMarketForm = lazy(() => import('./pages/admin/AdminMarketForm'));
const AdminMarketDetail = lazy(() => import('./pages/admin/AdminMarketDetail'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminUserDetail = lazy(() => import('./pages/admin/AdminUserDetail'));
const AdminData = lazy(() => import('./pages/admin/AdminData'));
const AdminWithdrawals = lazy(() => import('./pages/admin/AdminWithdrawals'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminUserOverview = lazy(() => import('./pages/admin/AdminUserOverview'));
const AdminBetRankings = lazy(() => import('./pages/admin/AdminBetRankings'));
const AdminFundFlow = lazy(() => import('./pages/admin/AdminFundFlow'));
const AdminCurrencyLogs = lazy(() => import('./pages/admin/AdminCurrencyLogs'));

/**
 * 判断当前是否在后台管理域名下
 * 仅 backend.augurx.win 允许访问 /admin 路由
 */
function isAdminDomain(): boolean {
  const hostname = window.location.hostname;
  return hostname === 'backend.augurx.win' || hostname === 'test-backend.augurx.win' || hostname === 'localhost' || hostname === '127.0.0.1';
}

/**
 * 路由配置组件
 * 定义所有页面路由映射关系
 */
function Router() {
  const adminAllowed = isAdminDomain();
  return (
    <Switch>
      {/* 后台域名：根路径直接跳到后台登录页 */}
      {adminAllowed && <Route path="/"><Redirect to="/admin/login" /></Route>}
      {/* 首页（仅非后台域名） */}
      {!adminAllowed && <Route path="/" component={Home} />}

      {/* 市场相关 */}
      <Route path="/markets" component={Markets} />
      <Route path="/markets/:id" component={MarketDetail} />

      {/* 账户体系 */}
      <Route path="/login" component={Login} />
      <Route path="/register" component={Register} />
      <Route path="/profile" component={Profile} />

      {/* 持仓与錢包 */}
      <Route path="/portfolio" component={Portfolio} />
      <Route path="/wallet">
        {/* Wallet 页面懒加载，避免 @tonconnect/ui-react 在其他页面加载 */}
        <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" /></div>}>
          <Wallet />
        </Suspense>
      </Route>

      {/* 排行榜 */}
      <Route path="/leaderboard" component={Leaderboard} />

      {/* 邀請好友 */}
      <Route path="/invite" component={Invite} />
      {/* 关于我们 */}
      <Route path="/about" component={About} />

      {/* 法律与帮助 */}
      <Route path="/terms" component={TermsOfService} />
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/help" component={HelpCenter} />

      {/* 后台管理：仅 backend.augurx.win 域名可访问，其他域名重定向到首页 */}
      <Route path="/admin/login">{adminAllowed ? <AdminLogin /> : <Redirect to="/" />}</Route>
      <Route path="/admin">{adminAllowed ? <AdminDashboard /> : <Redirect to="/" />}</Route>
      <Route path="/admin/markets">{adminAllowed ? <AdminMarkets /> : <Redirect to="/" />}</Route>
      <Route path="/admin/markets/new">{adminAllowed ? <AdminMarketForm /> : <Redirect to="/" />}</Route>
      <Route path="/admin/markets/:id/edit">{adminAllowed ? <AdminMarketForm /> : <Redirect to="/" />}</Route>
      <Route path="/admin/markets/:id">{adminAllowed ? <AdminMarketDetail /> : <Redirect to="/" />}</Route>
      <Route path="/admin/users">{adminAllowed ? <AdminUsers /> : <Redirect to="/" />}</Route>
      <Route path="/admin/users/:id">{adminAllowed ? <AdminUserDetail /> : <Redirect to="/" />}</Route>
      <Route path="/admin/data">{adminAllowed ? <AdminData /> : <Redirect to="/" />}</Route>
      <Route path="/admin/withdrawals">{adminAllowed ? <AdminWithdrawals /> : <Redirect to="/" />}</Route>
      <Route path="/admin/user-overview">{adminAllowed ? <AdminUserOverview /> : <Redirect to="/" />}</Route>
      <Route path="/admin/bet-rankings">{adminAllowed ? <AdminBetRankings /> : <Redirect to="/" />}</Route>
      <Route path="/admin/fund-flow">{adminAllowed ? <AdminFundFlow /> : <Redirect to="/" />}</Route>
      <Route path="/admin/currency-logs">{adminAllowed ? <AdminCurrencyLogs /> : <Redirect to="/" />}</Route>
      <Route path="/admin/settings">{adminAllowed ? <AdminSettings /> : <Redirect to="/" />}</Route>

      {/* 404 */}
      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

/**
 * Token 過期監聽器：監聽全局 token-expired 事件，彈出 toast 并跳转登入
 */
function TokenExpiredHandler() {
  const { logout, isAuthenticated } = useAppStore();
  const [, navigate] = useLocation();
  const { t } = useTranslation();
  const handledRef = useRef(false);

  useEffect(() => {
    const handler = () => {
      // 未登录狀態下不需要處理
      if (!isAuthenticated) return;
      // 防止重複觸發
      if (handledRef.current) return;
      handledRef.current = true;
      setTimeout(() => { handledRef.current = false; }, 3000);

      logout();
      toast.error(t('tokenExpired.title'), {
        description: t('tokenExpired.message'),
        duration: 4000,
        action: {
          label: t('tokenExpired.action'),
          onClick: () => navigate('/login'),
        },
      });
      navigate('/login');
    };

    window.addEventListener('token-expired', handler);
    return () => window.removeEventListener('token-expired', handler);
  }, [isAuthenticated, logout, navigate, t]);

  return null;
}

/**
 * 全局余额轮询：登录状态下每秒刷新一次余额
 */
function BalancePoller() {
  const { isAuthenticated, refreshUserInfo } = useAppStore();
  useEffect(() => {
    if (!isAuthenticated) return;
    // 立即刷新一次
    refreshUserInfo();
    const timer = setInterval(() => {
      refreshUserInfo();
    }, 1000);
    return () => clearInterval(timer);
  }, [isAuthenticated, refreshUserInfo]);
  return null;
}

/**
 * 应用根组件
 * 提供全局主题（深色模式）、Tooltip、Toast 等基础设施
 */
function App() {
  return (
    <ErrorBoundary>
      <TonConnectUIProvider
        manifestUrl={TON_MANIFEST_URL}
        actionsConfiguration={{
          twaReturnUrl: DEFAULT_TWA_RETURN_URL,
          returnStrategy: 'back',
        }}
      >
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <Toaster
            position="top-right"
            closeButton
            toastOptions={{
              style: {
                background: '#1E2026',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#EAECEF',
              },
            }}
          />
          <TokenExpiredHandler />
          <BalancePoller />
          <TelegramAutoLogin />
          <ServerStatusGuard />
          <DebugConsole />
          <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" /></div>}>
            <Router />
          </Suspense>
        </TooltipProvider>
      </ThemeProvider>
      </TonConnectUIProvider>
    </ErrorBoundary>
  );
}

export default App;
